// src/pages/CategoryLink.jsx
// ─── KEY FIX: never call setApiLanguage during render ───────────

import React, { useCallback, useEffect, useState, lazy, Suspense, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import PauseLink from '../Component/PauseLink';
import AllHintPage from '../Component/CategoryLink/allhintpage';
import Nouser from "../img/NoUserFound.png";
import NoNet from "../img/NoNet.png";
import NouserDark from "../img/NoUserFoundDark.png";
import NoNetDark from "../img/NoNetDark.png";
import NoDataFound2 from '../Component/NoData2';
import NoInternet from '../Component/NoInternet';
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from '../Component/context/Languagecontext';
import LanguageDropdown from '../Component/Languagedropdown';
import instagram from "./../video/instagram.mp4";
import snap from "./../video/snap.mp4";

const NoUserFound = lazy(() => import('../Component/NoUser'));
const NoDataFound = lazy(() => import('../Component/NoData'));

// ── image caching util (unchanged) ──────────────────────────────
const storeImageInLocalStorage = (key, url) => {
    const stored = localStorage.getItem(key);
    if (stored) return Promise.resolve(stored);
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = url;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            canvas.getContext('2d').drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL('image/png');
            localStorage.setItem(key, dataURL);
            resolve(dataURL);
        };
        img.onerror = reject;
    });
};

const getInitialDarkMode = () =>
    typeof window !== 'undefined'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
        : false;

// ── component ────────────────────────────────────────────────────
const CategoryLink = () => {
    const { username, category, timestamp } = useParams();

    // ✅ Get t, from context — these update when user picks a language
    const { t, setApiLanguage } = useLanguage();
    

    const [data2, setData2] = useState([]);
    const [pauseLink, setPauseLink] = useState(null);
    const [block, setBlock] = useState(null);
    const [notAllowed, setnotAllowed] = useState(null);
    const [usernot, setUsernot] = useState(null);
    const [inboxDoc, setinboxDoc] = useState(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [noNetImgLoaded, setNoNetImgLoaded] = useState(false);
    const [noUserImgSrc, setNoUserImgSrc] = useState(null);
    const [noNetImgSrc, setNoNetImgSrc] = useState(null);
    const [noUserDarkImgSrc, setNoUserDarkImgSrc] = useState(null);
    const [noNetDarkImgSrc, setNoNetDarkImgSrc] = useState(null);
    const [isDarkMode, setIsDarkMode] = useState(getInitialDarkMode);
    const [showNoData, setShowNoData] = useState(false);
    const [micPermissionChecked, setMicPermissionChecked] = useState(false);
    const [alertModal, setAlertModal] = useState({
        show: false, message: '', title: 'Alert', showBrowserInstructions: false
    });
    const [snackbar, setSnackbar] = useState({
        show: false, message: '', type: 'error'
    });

    const isValidCategory = [
        "V2hvVGFsa2lu", "UGljIFJvYXN0", "QW5ub3kgZnVuIENhcmQ=", "RW1vdGlvbg==",
        "Q29uZmVzc2lvbg==", "SG90bmVzcw==", "RnJpZW5k", "Um9hc3Q=",
        "UnVtb3Vy", "Q2hhbGxlbmdl", "SGVhdmVuSGVsbA==", "UmVwdXRhdGlvbg==", "SW1wcmVzc2lvbg=="
    ].includes(category);

    const micCategories = ["V2hvVGFsa2lu", "RW1vdGlvbg==", "Q29uZmVzc2lvbg==", "Um9hc3Q=", "RnJpZW5k"];

    const memoizedData2 = useMemo(() => data2, [data2]);
    const user = memoizedData2?.question;

    // ── subCat: derived from data, not language ──────────────────
    const subCat = useMemo(() => {
        if (!user || !Array.isArray(user)) return "RnJpZW5k";
        if (category !== "RnJpZW5k") return "RnJpZW5k";
        return user.find(item => item.category === category)?.answer?.subCategory ?? "RnJpZW5k";
    }, [user, category]);

    // ✅ FIX: setApiLanguage in useEffect — NEVER call state setters during render
    useEffect(() => {
        if (!user || !Array.isArray(user)) {
            setApiLanguage('en');
            return;
        }

        let apiLan;
        if (category === "UGljIFJvYXN0") {
            apiLan = user.find(
                item => item.category === category && String(item.answer?.timestamp) === String(timestamp)
            )?.lan || 'en';
        } else {
            apiLan = user.find(item => item.category === category)?.lan || 'en';
        }

        setApiLanguage(apiLan);   // context ignores this if user already picked manually
    }, [user, category, timestamp, setApiLanguage]);

    // ── dark mode ────────────────────────────────────────────────
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        setIsDarkMode(mq.matches);
        const handler = (e) => setIsDarkMode(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    // ── analytics ────────────────────────────────────────────────
    useEffect(() => {
        const key = `analytics_called_${username}_${category}`;
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, '1');
        axios.post('https://api.lolcards.link/api/web/view/count', { username, category }).catch(() => { });
    }, [username, category]);

    const currentNoUserImg = isDarkMode ? noUserDarkImgSrc : noUserImgSrc;
    const currentNoNetImg = isDarkMode ? noNetDarkImgSrc : noNetImgSrc;

    // ── internet check ───────────────────────────────────────────
    const checkInternetConnection = async () => {
        if (!navigator.onLine) return false;
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            clearTimeout(timeoutId);
            return true;
        } catch {
            return false;
        }
    };

    // ── fetch data ───────────────────────────────────────────────
    const getData2 = useCallback(async () => {
        localStorage.removeItem('micDeniedCount');
        const isOnline = await checkInternetConnection();
        if (!isOnline) { setShowNoData(true); setLoading(false); return; }

        setLoading(true);
        const deviceIp = localStorage.getItem("deviceIp");

        axios.post('https://api.lolcards.link/api/web/catgory/found', {
            username, category, timestamp, ip: deviceIp
        })
            .then(({ data }) => {
                setData2(data.data);
                setBlock(data.block);
                setnotAllowed(data.notAllowed);
                setUsernot(data.usernot);
                setinboxDoc(data.inboxDoc);
                setPauseLink(data.data.pauseLink);
                setError(false);
                // ✅ language is set via the useEffect above that watches `user`
            })
            .catch(() => { toast.error("Failed to fetch data."); setError(true); })
            .finally(() => setLoading(false));
    }, [username, category, timestamp]);

    const getIp = useCallback(async () => {
        const isOnline = await checkInternetConnection();
        if (!isOnline) { setShowNoData(true); setLoading(false); return; }

        setLoading(true);
        axios.post('https://api.lolcards.link/api/web/catgory/ip')
            .then(({ data }) => {
                if (data?.ip) { localStorage.setItem("deviceIp", data.ip); getData2(); }
                setError(false);
            })
            .catch(() => { toast.error("Failed to fetch IP."); })
            .finally(() => setLoading(false));
    }, [getData2]);

    useEffect(() => {
        const checkInitialConnection = async () => {
            const isOnline = await checkInternetConnection();
            if (!isOnline) setShowNoData(true);
        };
        checkInitialConnection();
    }, []);

    useEffect(() => {
        const storedIp = localStorage.getItem("deviceIp");
        if (storedIp) getData2(); else getIp();
    }, [username, category, isValidCategory, getData2, getIp]);

    // ── device helpers (unchanged) ───────────────────────────────
    const getDeviceType = () => {
        const ua = navigator.userAgent || navigator.vendor || window.opera;
        if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
        if (/android/i.test(ua)) return 'android';
        return 'other';
    };
    const getAndroidVersion = () => {
        const match = (navigator.userAgent || '').match(/Android\s+([\d.]+)/i);
        return match ? parseInt(match[1].split('.')[0], 10) : null;
    };
    const isInAppBrowser = () =>
        /Instagram|FBAN|FBAV|Messenger|Telegram|Snapchat|Snap/i.test(
            navigator.userAgent || navigator.vendor || window.opera
        );
    const getUserMediaWithTimeout = (constraints, timeout = 2000) =>
        Promise.race([
            navigator.mediaDevices.getUserMedia(constraints),
            new Promise((_, reject) => setTimeout(() => reject(new Error("MIC_TIMEOUT")), timeout))
        ]);

    // ── mic check (unchanged) ────────────────────────────────────
    const checkMicrophonePermission = useCallback(async () => {
        if (!micCategories.includes(category)) { setMicPermissionChecked(true); return; }
        if (!isInAppBrowser() || getDeviceType() !== 'android') { setMicPermissionChecked(true); return; }

        const androidVersion = getAndroidVersion();
        const ua = navigator.userAgent || navigator.vendor || window.opera;
        const isInstagram = /Instagram/i.test(ua);
        const isSnapchat = /Snapchat|Snap/i.test(ua);
        const modalDelay = (isInstagram && androidVersion !== null && androidVersion <= 9) ? 0 : 30000;

        if (isSnapchat) {
            setAlertModal({ show: true, message: '', title: 'Microphone Access Required', showBrowserInstructions: true });
            return;
        }

        try {
            const stream = await getUserMediaWithTimeout({ audio: true }, 2000);
            stream.getTracks().forEach(track => track.stop());
            setMicPermissionChecked(true);
        } catch (err) {
            if (err.message === "MIC_TIMEOUT") {
                let msg = t.microErrorDefault || "Microphone not responding. Please open in Safari or Chrome.";
                if (/Instagram/i.test(ua)) msg = t.microErrorInstagram || msg;
                else if (/Snapchat|Snap/i.test(ua)) msg = t.microErrorSnapchat || msg;
                else if (/FBAN|FBAV|Messenger/i.test(ua)) msg = t.microErrorFacebook || msg;
                else if (/Telegram/i.test(ua)) msg = t.microErrorTelegram || msg;
                setTimeout(() => setAlertModal({ show: true, message: msg, title: 'Microphone Access Required', showBrowserInstructions: true }), modalDelay);
                setMicPermissionChecked(true);
                return;
            }
            if (err.name === 'NotFoundError') {
                setTimeout(() => setAlertModal({ show: true, message: "No microphone found on your device.", title: 'Microphone Not Found', showBrowserInstructions: false }), modalDelay);
                setMicPermissionChecked(true);
                return;
            }
            setTimeout(() => setAlertModal({ show: true, message: "Microphone access is required. Please open this link in your phone's browser.", title: 'Microphone Access Required', showBrowserInstructions: true }), modalDelay);
            setMicPermissionChecked(true);
        }
    }, [category, username, t]);

    useEffect(() => {
        if (!loading && micCategories.includes(category) && !micPermissionChecked &&
            !error && !pauseLink && !block && !notAllowed && !usernot) {
            checkMicrophonePermission();
        }
    }, [loading, category, micPermissionChecked, error, pauseLink, block, notAllowed, usernot, checkMicrophonePermission]);

    // ── image preloading (unchanged) ─────────────────────────────
    useEffect(() => {
        let isMounted = true;
        const load = (key, src, setter, flagSetter) => {
            storeImageInLocalStorage(key, src)
                .then(url => { if (isMounted) { setter(url); if (flagSetter) flagSetter(true); } })
                .catch(() => { if (isMounted) { setter(src); if (flagSetter) flagSetter(true); } });
        };
        load('noUserImage', Nouser, setNoUserImgSrc);
        load('noNetImage', NoNet, setNoNetImgSrc, setNoNetImgLoaded);
        load('noUserImageDark', NouserDark, setNoUserDarkImgSrc);
        load('noNetImageDark', NoNetDark, setNoNetDarkImgSrc);
        return () => { isMounted = false; };
    }, []);

    // ── early returns ────────────────────────────────────────────
    if (showNoData) return <Suspense fallback={<div />}><NoInternet style={{ height: '100vh' }} t={t.noData} Nouser={currentNoNetImg} imgLoaded={noNetImgLoaded} /></Suspense>;
    if (usernot) return <Suspense fallback={<div />}><NoUserFound Nouser={currentNoUserImg} /></Suspense>;
    if (pauseLink) return <Suspense fallback={<div />}><PauseLink t={t.pauseLink} Nouser={currentNoUserImg} /></Suspense>;
    if (block) return <Suspense fallback={<div />}><NoDataFound style={{ height: '100vh' }} t={t.noData} Nouser={currentNoUserImg} /></Suspense>;
    if (notAllowed) return <Suspense fallback={<div />}><NoDataFound style={{ height: '100vh' }} t={t.notAllowed} Nouser={currentNoUserImg} /></Suspense>;
    if (error) return <Suspense fallback={<div />}><NoDataFound2 style={{ height: '100vh' }} t={t.noData2} Nouser={currentNoUserImg} username={username} category={category} timestamp={timestamp} /></Suspense>;

    return (
        <>
            {/* ✅ Dropdown is position:fixed — floats over everything */}
            <LanguageDropdown />

            <Suspense fallback={<div />}>
                {isValidCategory ? (
                    <AllHintPage
                        data={memoizedData2}
                        inboxDoc={inboxDoc}
                        Nouser={currentNoUserImg}
                    />
                ) : (
                    <NoUserFound Nouser={currentNoUserImg} />
                )}
            </Suspense>

            {/* Alert Modal (unchanged) */}
            {alertModal.show && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}>
                    <div className="popup-bg rounded-4 p-3 m-3"
                        style={{ maxWidth: alertModal.showBrowserInstructions ? '500px' : '400px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '90vh', overflowY: 'auto' }}
                        onClick={e => e.stopPropagation()}>

                        <div className="text-center mb-3">
                            <div className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                style={{
                                    width: '60px', height: '60px',
                                    border: (() => {
                                        if (category === "RnJpZW5k") return subCat === "RnJpZW5k" ? "8px solid #F5F4FF" : "8px solid #FFD5DA";
                                        if (category === "V2hvVGFsa2lu") return "8px solid #D5C8F8";
                                        if (category === "RW1vdGlvbg==") return "8px solid #FFD8A8";
                                        if (category === "Q29uZmVzc2lvbg==") return "8px solid #E9CAFB";
                                        if (category === "Um9hc3Q=") return "8px solid #FDD9BF";
                                        return "#FEF3F2";
                                    })(),
                                    backgroundColor: (() => {
                                        if (category === "RnJpZW5k") return subCat === "RnJpZW5k" ? "#B3ADFD" : "#FF617A";
                                        if (category === "V2hvVGFsa2lu") return "#684BB9";
                                        if (category === "RW1vdGlvbg==") return "#FFA400";
                                        if (category === "Q29uZmVzc2lvbg==") return "#8001CE";
                                        if (category === "Um9hc3Q=") return "#F98423";
                                        return "#FEF3F2";
                                    })()
                                }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                                        stroke='#fff' strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>

                        {alertModal.showBrowserInstructions && (
                            <h5 className="mb-3 popup-main-color fw-bold text-center" style={{ fontSize: '18px' }}>
                                {t.microtitle || 'Microphone Access Required'}
                            </h5>
                        )}
                        {alertModal.showBrowserInstructions && (
                            <div className="mt-4">
                                <p className='small mb-1 ms-1 popup-main-color' style={{ fontWeight: "600" }}>{t.follow || "Follow this step"} :</p>
                                {getDeviceType() === 'ios' && (
                                    <div className="popup-center-bg rounded-3 p-3 mb-3" style={{ height: "200px" }}>
                                        <video autoPlay loop muted playsInline style={{ width: '100%', maxWidth: '400px', borderRadius: '8px', backgroundColor: '#000' }}>
                                            <source src={instagram} type="video/mp4" />
                                        </video>
                                    </div>
                                )}
                                {getDeviceType() === 'android' && (
                                    <div className="popup-center-bg rounded-3 p-3 mb-3">
                                        <video autoPlay loop muted playsInline style={{ width: '100%', maxWidth: '400px', borderRadius: '8px', backgroundColor: '#000' }}>
                                            <source src={/Instagram/i.test(navigator.userAgent) ? instagram : /Snapchat|Snap/i.test(navigator.userAgent) ? snap : instagram} type="video/mp4" />
                                        </video>
                                    </div>
                                )}
                                <div className="text-center mt-3">
                                    <p className="mb-2" style={{ fontSize: '12px', color: "#A7A7A7" }}>
                                        {t.orCopyLink || 'Or copy this link and paste in browser'}
                                    </p>
                                    <div className="popup-border-dashed d-flex align-items-center justify-content-center bg-trasparent rounded-3 p-2">
                                        <span className="text-truncate me-2" style={{ fontSize: "12px", color: "#A7A7A7" }}>
                                            {`https://lolcards.link/${username}/${category}`}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="text-center mt-4">
                            <button className="btn px-5 py-2 fw-semibold"
                                style={{
                                    backgroundColor: (() => {
                                        if (category === "RnJpZW5k") return subCat === "RnJpZW5k" ? "#B3ADFD" : "#FF617A";
                                        if (category === "V2hvVGFsa2lu") return "#684BB9";
                                        if (category === "RW1vdGlvbg==") return "#FFA400";
                                        if (category === "Q29uZmVzc2lvbg==") return "#8001CE";
                                        if (category === "Um9hc3Q=") return "#F98423";
                                        return "#FEF3F2";
                                    })(),
                                    color: 'white', border: 'none', borderRadius: '8px', width: '100%', fontSize: '14px'
                                }}
                                onClick={() => {
                                    if (alertModal.showBrowserInstructions) {
                                        navigator.clipboard.writeText(`https://lolcards.link/${username}/${category}`);
                                        setSnackbar({ show: true, message: t.linkCopied, type: 'error' });
                                        setTimeout(() => setSnackbar({ show: false, message: '', type: 'error' }), 3000);
                                    }
                                }}>
                                {t.gotIt || 'Copy Link'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <AnimatePresence>
                {snackbar.show && (
                    <motion.div
                        className={`snackbar d-flex align-item-center gap-3 ${snackbar.type}`}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    >
                        <div className="snackbar-icon px-3">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <circle cx="10" cy="10" r="9" stroke="#fff" strokeWidth="2" fill="none" />
                                <path d="M10 6V11" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                                <circle cx="10" cy="14" r="1" fill="#fff" />
                            </svg>
                        </div>
                        <div className="snackbar-message">{snackbar.message}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};


export default CategoryLink;